import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { XIcon } from "lucide-react";

import {
  Center,
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  Text,
} from "@/components";
import {
  EmailComposer,
  type SendEmailComposerValues,
} from "@jobs/emails/EmailComposer";
import { EmailsList } from "@jobs/emails/EmailsList";
import { ScheduledAgentEmailRow } from "@jobs/emails/ScheduledAgentEmailRow";
import { StafferQuoMessages } from "@jobs/quo-messages/StafferQuoMessages";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  UserSheetCommsCandidacy,
  UserSheetCommsTab,
} from "@/components/user-sheet/types";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useSendEmail } from "@/hooks/mutations/useSendEmail";
import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { useGetEmails } from "@/hooks/queries/useGetEmails";
import { useGetQuoMessages } from "@/hooks/queries/useGetQuoMessages";
import { useGetScheduledAgentMessages } from "@/hooks/queries/useGetScheduledAgentMessages";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { commsTabTriggerClassName } from "@/lib/constants";
import { email } from "@/lib/email";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import {
  Email,
  PingType,
  StafferAgentScheduledMessageChannel,
  VoiceAIOutreachStatus,
} from "@/types";

const MAX_DISPLAY_MESSAGE_COUNT = 9;

// Candidate-facing comms: the Messages/Email tab pair with composers. The whole
// panel for the plain user sheet; the Candidate scope inside the candidacy rail.
// `candidacy` scopes SMS/email to the right talent and enables the
// status-transition/agent-takeover behavior — pass it whenever the sheet is
// candidacy-backed. Drafts are optionally lifted so they survive scope switches.
export const CandidateCommsTabs = ({
  candidacy,
  emailDraft,
  isMessagesOnly = false,
  isReadOnly = false,
  messageDraft,
  onEmailDraftChange,
  onMessageDraftChange,
  showMobileClose = false,
}: {
  candidacy?: UserSheetCommsCandidacy;
  emailDraft?: SendEmailComposerValues;
  isMessagesOnly?: boolean;
  // Search and the application review portal show the history only — nobody
  // should be able to reach out to a candidate from either surface.
  isReadOnly?: boolean;
  messageDraft?: string;
  onEmailDraftChange?: (values: SendEmailComposerValues) => void;
  onMessageDraftChange?: (body: string) => void;
  // Read-only mobile panels put the collapse X on this tab row (name lives above).
  showMobileClose?: boolean;
}) => {
  /* Context */
  const {
    candidacyId,
    closeCommunications,
    communicationsTab,
    isCommunicationsOpen,
    jobId,
    openCommunications,
    workerId,
  } = useUserSheet();
  const isMobile = useIsMobile();
  const tabTriggerClassName = cn(
    commsTabTriggerClassName,
    isMobile && "hover:bg-transparent hover:text-slate-500",
  );

  /* Helpers */
  // Without a candidacy (the search sheet), the worker is an active talent.
  const talentId = candidacy ? candidacy.talentId : workerId;

  /* Database CRUD Operations */
  // Read
  const userApi = useGetCandidacySheetData({ jobId, userId: workerId });
  const emailsQuery = useGetEmails({
    talentId: isMessagesOnly ? null : talentId,
  });
  const messagesQuery = useGetQuoMessages({ talentId });
  const scheduledMessagesQuery = useGetScheduledAgentMessages({
    candidacyId: candidacyId ?? null,
  });
  // Same query the Records tab uses, so calls are fetched once per sheet.
  const callHistoryQuery = useGetCallHistory(talentId ?? 0);

  // Create
  const { isPending: isSendingEmail, mutate: sendEmail } = useSendEmail();

  /* Cache Updates */
  // Set
  const setCandidacyInfo = useSetCandidacyInfo();

  /* React Hooks */
  const queryClient = useQueryClient();
  // Fallback draft state for mounts that don't lift the drafts.
  const [localEmailDraft, setLocalEmailDraft] =
    useState<SendEmailComposerValues>({ body: "", subject: "" });
  const [openThreadIds, setOpenThreadIds] = useState<string[]>([]);
  const [replyToEmail, setReplyToEmail] = useState<Email | null>(null);
  const threads = useMemo(
    () => email.groupIntoThreads(emailsQuery.data ?? []),
    [emailsQuery.data],
  );

  /* Helpers */
  const activeEmailDraft = emailDraft ?? localEmailDraft;
  const handleEmailDraftChange = onEmailDraftChange ?? setLocalEmailDraft;
  const firstName = userApi.data?.profile.firstName;
  const candidateFirstName = firstName ?? "Candidate";
  const candidateName = formatting.fullName(
    firstName,
    userApi.data?.profile.lastName,
    "Candidate",
  );
  const scheduledEmail = scheduledMessagesQuery.data?.find(
    (scheduledMessage) =>
      scheduledMessage.channel === StafferAgentScheduledMessageChannel.EMAIL,
  );
  const emailCount = emailsQuery.data?.length ?? 0;
  const displayEmailCount =
    emailCount === 0
      ? null
      : emailCount > MAX_DISPLAY_MESSAGE_COUNT
        ? `${MAX_DISPLAY_MESSAGE_COUNT.toString()}+`
        : String(emailCount);
  const messageCount = messagesQuery.data?.length ?? 0;
  const displayMessageCount =
    messageCount === 0
      ? null
      : messageCount > MAX_DISPLAY_MESSAGE_COUNT
        ? `${MAX_DISPLAY_MESSAGE_COUNT.toString()}+`
        : String(messageCount);

  /* Handlers */
  const handleSendEmail = (values: SendEmailComposerValues) => {
    sendEmail(
      {
        sendEmailRequest: {
          body: values.body,
          ...(candidacyId ? { candidacyId } : {}),
          ...(replyToEmail?.rfcMessageId
            ? {
                inReplyTo: {
                  gmailThreadId: replyToEmail.gmailThreadId,
                  rfcMessageId: replyToEmail.rfcMessageId,
                },
              }
            : {}),
          subject: values.subject,
          ...(talentId !== null ? { talentId } : {}),
        },
      },
      {
        onSuccess: (sentEmail) => {
          void queryClient.invalidateQueries({
            queryKey: ["emails", talentId],
          });
          if (!candidacyId) return;

          const lastPing = {
            at: sentEmail.sentAt,
            direction: sentEmail.direction,
            type: PingType.EMAIL,
            status: sentEmail.status,
            stafferId: sentEmail.userId,
            sentByAgent: sentEmail.sentByAgent,
            body: null,
          };
          setCandidacyInfo({
            candidacyId,
            lastCommunication: lastPing,
            ...(sentEmail.sentByAgent
              ? { lastAiCommunication: lastPing }
              : { lastHumanCommunication: lastPing }),
          });
        },
      },
    );
  };

  const handleToggleThread = (threadId: string) => {
    setOpenThreadIds((previous) => {
      if (previous.includes(threadId)) {
        return previous.filter((id) => id !== threadId);
      }
      return [...previous, threadId];
    });
  };

  /* Render */
  const renderEmailTab = () => {
    if (emailsQuery.isLoading) {
      return <LoadingIndicator variant="medium" />;
    }
    if (emailsQuery.isError) {
      return <ErrorText errorMessage="Error retrieving emails." />;
    }

    return (
      <Column className="h-full min-h-0 gap-0">
        <EmailsList
          candidateFirstName={candidateFirstName}
          candidateName={candidateName}
          onClearReplyTarget={() => {
            setReplyToEmail(null);
          }}
          onReply={
            isReadOnly
              ? undefined
              : (emailToReplyTo) => {
                  setOpenThreadIds([email.threadKey(emailToReplyTo)]);
                  setReplyToEmail(emailToReplyTo);
                }
          }
          onToggleThread={handleToggleThread}
          openThreadIds={openThreadIds}
          replyTarget={replyToEmail}
          threads={threads}
        />
        {scheduledEmail && (
          <ScheduledAgentEmailRow scheduledMessage={scheduledEmail} />
        )}
        {isReadOnly ? null : (
          <EmailComposer
            autoFocus={isCommunicationsOpen}
            candidateName={candidateName}
            disabled={isSendingEmail}
            emailDraft={activeEmailDraft}
            onClearReplyTarget={() => {
              setReplyToEmail(null);
            }}
            onEmailDraftChange={handleEmailDraftChange}
            onSend={handleSendEmail}
            replyTarget={replyToEmail}
          />
        )}
      </Column>
    );
  };

  const renderMessagesTab = () => {
    // Wait for call history so the interweaved timeline mounts complete and
    // the initial scroll-to-bottom lands on the real content height.
    if (callHistoryQuery.isLoading) {
      return <LoadingIndicator variant="medium" />;
    }

    return (
      <Column className="h-full min-h-0 gap-0">
        {callHistoryQuery.isError ? (
          <ErrorText errorMessage="Error retrieving calls for the timeline." />
        ) : null}
        <StafferQuoMessages
          // Pending/in-progress AI calls haven't produced anything to show and
          // may be scheduled in the future — keep them off the timeline.
          aiCalls={callHistoryQuery.data?.aiCalls.filter(
            (call) =>
              call.status !== VoiceAIOutreachStatus.PENDING.toString() &&
              call.status !== VoiceAIOutreachStatus.IN_PROGRESS.toString(),
          )}
          autoFocus={isCommunicationsOpen}
          calls={callHistoryQuery.data?.stafferCalls}
          candidacy={candidacy}
          isReadOnly={isReadOnly}
          messageDraft={messageDraft}
          onMessageDraftChange={onMessageDraftChange}
          phone={
            candidacy ? candidacy.phone : (userApi.data?.profile.phone ?? null)
          }
          talentId={talentId}
        />
      </Column>
    );
  };

  const renderTabLabel = ({
    count,
    hasUnread,
    label,
  }: {
    count?: string | null;
    hasUnread: boolean;
    label: string;
  }) => {
    return (
      <Row className="items-center gap-1.5">
        {label}
        {count ? (
          <Center className="h-4 min-w-4 rounded-full bg-blue-200/70 px-1">
            <Text className="text-[10px] font-normal text-blue-950">
              {count}
            </Text>
          </Center>
        ) : null}
        {hasUnread ? (
          <span className="block h-1.5 w-1.5 rounded-full bg-red-500" />
        ) : null}
      </Row>
    );
  };

  const render = () => {
    return (
      <Tabs
        className={cn(
          "flex min-h-0 w-full flex-1 flex-col overflow-hidden",
          isMobile && showMobileClose ? "pt-0" : "pt-3",
        )}
        onValueChange={(tab) => {
          openCommunications(tab as UserSheetCommsTab);
        }}
        // "comments" belongs to the Internal rail, so it is not a value this
        // Tabs can hold — showing nothing at all is worse than Messages.
        value={
          isMessagesOnly || communicationsTab === "comments"
            ? "messages"
            : communicationsTab
        }
      >
        <Column className="w-full px-2 pb-4">
          <Row className="w-full items-end border-b border-slate-300">
            <TabsList className="flex h-auto min-w-0 flex-1 justify-start gap-0 rounded-none border-b-0 bg-transparent p-0 text-slate-500 shadow-none">
              <TabsTrigger className={tabTriggerClassName} value="messages">
                {renderTabLabel({
                  count: displayMessageCount,
                  // The count pill already conveys presence — skip the dot.
                  hasUnread: false,
                  label: "Messages",
                })}
              </TabsTrigger>
              {isMessagesOnly ? null : (
                <TabsTrigger className={tabTriggerClassName} value="email">
                  {renderTabLabel({
                    count: displayEmailCount,
                    hasUnread: false,
                    label: "Email",
                  })}
                </TabsTrigger>
              )}
            </TabsList>
            {isMobile && showMobileClose ? (
              <Button
                className="h-12 w-12 shrink-0 text-slate-500 hover:bg-transparent hover:text-slate-500 [&_svg]:size-5"
                onClick={closeCommunications}
                size="icon"
                title="Collapse communications"
                type="button"
                variant="ghost"
              >
                <XIcon className="h-5 w-5" />
              </Button>
            ) : null}
          </Row>
        </Column>
        <TabsContent
          className="min-h-0 w-full flex-1 overflow-hidden px-3 pb-4"
          value="messages"
        >
          {renderMessagesTab()}
        </TabsContent>
        {isMessagesOnly ? null : (
          <TabsContent
            className="min-h-0 w-full flex-1 overflow-hidden px-3 pb-4"
            value="email"
          >
            {renderEmailTab()}
          </TabsContent>
        )}
      </Tabs>
    );
  };

  return render();
};
